// Congratulations screen — shown after the SF write succeeds, before the
// "Get Set Up for Success" form. Two-column layout: appointment details
// on the left, upsell + capture form on the right.
//
// Design comp reference: Screenshot 2026-05-25 6:18:58.

import styles from '../styles/widget.module.css';
import { formatSlotTime } from '../utils/datetime';
import type { BookingState } from '../state/machine';

interface BookingConfirmedProps {
  state: BookingState;
  children?: React.ReactNode;
}

export function BookingConfirmed({ state, children }: BookingConfirmedProps) {
  const conf = state.confirmation!;
  const firstName = conf.customer.firstName || state.fullName.split(/\s+/)[0] || 'Guest';
  const greetingName = state.fullName.trim() || `${conf.customer.firstName} ${conf.customer.lastName ?? ''}`.trim();
  // Waitlist vs confirmed booking — the middleware prefixes waitlist IDs with
  // `WL-` and sets status to 'waitlisted'. Either signal works; we check both.
  const isWaitlist = conf.status === 'waitlisted' || conf.id.startsWith('WL-');

  return (
    <div className={styles.congratsRoot}>
      <h2 className={styles.congratsTitle}>
        {isWaitlist ? 'You’re on the Waitlist' : 'Congratulations'}{' '}
        {greetingName ? greetingName.toUpperCase() : firstName.toUpperCase()}!
      </h2>
      <p className={styles.congratsSubtitle}>
        {isWaitlist
          ? `We’ll reach out as soon as a slot opens (${conf.customer.email}).`
          : `Your game is about to get better. Check your inbox (${conf.customer.email}).`}
      </p>

      <div className={styles.congratsGrid}>
        <div className={`${styles.congratsSection} ${styles.congratsSectionMainInfo}`}>
          <h4>{isWaitlist ? 'Your Waitlist Request' : 'Your Appointment'}</h4>
          <div className={styles.infoRow}>
            <p className={styles.rowLabel}>
              <strong>{isWaitlist ? 'Waitlist #' : 'Confirmation:'}</strong>{' '}
              {isWaitlist ? conf.id : `#${conf.id}`}
            </p>
          </div>
          <div className={styles.infoRow}>
            <p className={styles.rowLabel}><strong>{state.selectedLocation?.name}</strong></p>
            {state.selectedLocation?.address && <p>{state.selectedLocation.address}</p>}
          </div>
          <div className={styles.infoRow}>
            <p className={styles.rowLabel}>
              <strong>{isWaitlist ? 'Preferred Date' : 'Date & Time'}</strong>
            </p>
            <p>{state.selectedDate}</p>
            {!isWaitlist && (
              <>
                <p>{state.selectedTime ? formatSlotTime(state.selectedTime) : ''}</p>
                <a className={styles.addToCalendar} href="#" onClick={(e) => e.preventDefault()}>
                  Add to Calendar
                </a>
              </>
            )}
          </div>
        </div>
        <div className={styles.congratsSection}>
          {children}
        </div>
        <div className={`${styles.congratsSection} ${styles.questionsColWrapper}`}>
          <h4>Questions?</h4>
          <a href="tel:8883407820" className={styles.contactTel}>(888) 340-7820</a>
        </div>
      </div>
    </div>
  );
}
